/**
 * Auth-Store (Zustand) — Token-Provider, User-Info, Rollen.
 *
 * Zwei Modi (VITE_AUTH_MODE):
 *  - "logto" (Default): Der AuthProvider (src/auth/) registriert einen
 *    Token-Provider (Logto-SDK); der Axios-Interceptor holt darueber das
 *    Bearer-Token. Token-Refresh macht @logto/react selbst.
 *  - "dev": Kein Logto noetig. Der Interceptor sendet stattdessen den Header
 *    X-Dev-User mit einer JSON-Identitaet (konfigurierbar ueber
 *    localStorage-Key "devUser"), das Backend vertraut ihm nur im Dev-Betrieb.
 *
 * Merke (Coding-Standards): Rollen im Frontend nur zum Ein-/Ausblenden nutzen —
 * durchgesetzt wird immer serverseitig.
 */

import { create } from "zustand"
import type { DevUser } from "@/types"

// =============================================================================
// AUTH-MODUS + DEV-IDENTITAET
// =============================================================================

export const AUTH_MODE: "dev" | "logto" | "authentik" =
  import.meta.env.VITE_AUTH_MODE === "dev"
    ? "dev"
    : import.meta.env.VITE_AUTH_MODE === "authentik"
      ? "authentik"
      : "logto"

const DEFAULT_DEV_USER: DevUser = {
  sub: "dev",
  name: "Dev Admin",
  roles: ["admin"],
}

/** Dev-Identitaet: localStorage "devUser" (JSON) mit Fallback auf Dev Admin */
export function getDevUser(): DevUser {
  try {
    const raw = localStorage.getItem("devUser")
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DevUser>
      return {
        sub: parsed.sub ?? DEFAULT_DEV_USER.sub,
        name: parsed.name ?? DEFAULT_DEV_USER.name,
        roles: Array.isArray(parsed.roles) ? parsed.roles : DEFAULT_DEV_USER.roles,
      }
    }
  } catch {
    // Ungueltiges JSON -> Fallback
  }
  return DEFAULT_DEV_USER
}

// =============================================================================
// TOKEN-PROVIDER (wird vom AuthProvider registriert, ausserhalb von React nutzbar)
// =============================================================================

type TokenProvider = () => Promise<string | null>

let _tokenProvider: TokenProvider | null = null

export function setTokenProvider(provider: TokenProvider | null): void {
  _tokenProvider = provider
}

export async function getAccessToken(): Promise<string | null> {
  if (!_tokenProvider) return null
  try {
    return await _tokenProvider()
  } catch {
    return null
  }
}

// =============================================================================
// RE-LOGIN + LOGOUT (werden vom AuthProvider im Logto-Modus registriert)
// =============================================================================

type AuthActionHandler = () => void | Promise<void>

let _reloginHandler: AuthActionHandler | null = null
let _logoutHandler: AuthActionHandler | null = null
let _reloginAttempted = false

/** Vom AuthProvider registriert: startet einen frischen signIn-Redirect. */
export function setReloginHandler(handler: AuthActionHandler | null): void {
  _reloginHandler = handler
}

/** Vom AuthProvider registriert: Logto-signOut mit Post-Logout-Redirect. */
export function setLogoutHandler(handler: AuthActionHandler | null): void {
  _logoutHandler = handler
}

/**
 * 401 vom Backend (abgelaufene Session): genau EIN Re-Login-Versuch pro
 * Seitenaufruf — der signIn-Redirect verlaesst die Seite ohnehin; die Sperre
 * verhindert Redirect-Schleifen, falls der Login selbst 401 produziert.
 */
export function triggerRelogin(): void {
  if (AUTH_MODE === "dev") return
  if (_reloginAttempted || !_reloginHandler) return
  _reloginAttempted = true
  void _reloginHandler()
}

/** Abmelden — im Dev-Modus nur Store leeren, im Logto-Modus signOut-Redirect. */
export async function performLogout(): Promise<void> {
  if (AUTH_MODE === "logto" && _logoutHandler) {
    await _logoutHandler()
    return
  }
  useAuthStore.getState().logout()
}

// =============================================================================
// STORE
// =============================================================================

export interface AuthUser {
  id: string
  name: string
  email: string | null
  roles: string[]
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean

  setUser: (user: AuthUser | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void

  hasRole: (role: string) => boolean
  isAdmin: () => boolean
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Im Dev-Modus ist die Identitaet sofort da — kein Login-Roundtrip
  user:
    AUTH_MODE === "dev"
      ? (() => {
          const dev = getDevUser()
          return { id: dev.sub, name: dev.name, email: null, roles: dev.roles }
        })()
      : null,
  isAuthenticated: AUTH_MODE === "dev",
  isLoading: false,

  setUser: (user) => set({ user, isAuthenticated: user !== null }),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () => set({ user: null, isAuthenticated: false, isLoading: false }),

  hasRole: (role) => {
    const { user } = get()
    return user?.roles.includes(role) ?? false
  },
  isAdmin: () => get().hasRole("admin"),
}))
